/**
 * Lugar da pessoa para as sugestões de primeira busca: a cidade que o painel achou pelo IP no
 * primeiro carregamento (requestContext → `requestCtx` no localStorage, ver lib/fingerprint.ts).
 *
 * O IP pode falhar (bloqueador, rede da empresa) ou errar a cidade (internet móvel, VPN), por
 * isso o lugar só PRÉ-PREENCHE a busca: a pessoa troca na tela da busca. Sem cidade, as
 * sugestões ficam sem lugar e ela escolhe lá.
 */
import { getRequestContext } from '../../lib/requestContext';
import { BRAZIL_STATES, type BrazilState } from '../home/brazilStates';
import { norm } from './catalog';

export interface UserPlace {
  city?: string;
  /** Nome do estado/região como veio do IP. */
  region?: string;
  /** ISO de 2 letras, maiúsculo. */
  country?: string;
  /** "lat,lng" aproximado do IP. */
  geo?: string;
  /** Só no Brasil. */
  state?: BrazilState;
  /** DDD da cidade (só quando dá para ter certeza). */
  ddd?: string;
  /** DDDs do estado. */
  ddds: string[];
}

/** DDD → cidade principal/UF (config/brazil.json do backend). */
const DDD_TABLE: Array<[string, string, string]> = [
  ['11', 'São Paulo', 'SP'],
  ['12', 'São José dos Campos', 'SP'],
  ['13', 'Santos', 'SP'],
  ['14', 'Bauru', 'SP'],
  ['15', 'Sorocaba', 'SP'],
  ['16', 'Ribeirão Preto', 'SP'],
  ['17', 'São José do Rio Preto', 'SP'],
  ['18', 'Presidente Prudente', 'SP'],
  ['19', 'Campinas', 'SP'],
  ['21', 'Rio de Janeiro', 'RJ'],
  ['22', 'Campos dos Goytacazes', 'RJ'],
  ['24', 'Volta Redonda', 'RJ'],
  ['27', 'Vitória|Vila Velha', 'ES'],
  ['28', 'Cachoeiro de Itapemirim', 'ES'],
  ['31', 'Belo Horizonte', 'MG'],
  ['32', 'Juiz de Fora', 'MG'],
  ['33', 'Governador Valadares', 'MG'],
  ['34', 'Uberlândia', 'MG'],
  ['35', 'Poços de Caldas', 'MG'],
  ['37', 'Divinópolis', 'MG'],
  ['38', 'Montes Claros', 'MG'],
  ['41', 'Curitiba', 'PR'],
  ['42', 'Ponta Grossa', 'PR'],
  ['43', 'Londrina', 'PR'],
  ['44', 'Maringá', 'PR'],
  ['45', 'Foz do Iguaçu', 'PR'],
  ['46', 'Francisco Beltrão|Pato Branco', 'PR'],
  ['47', 'Joinville', 'SC'],
  ['48', 'Florianópolis', 'SC'],
  ['49', 'Chapecó', 'SC'],
  ['51', 'Porto Alegre', 'RS'],
  ['53', 'Pelotas', 'RS'],
  ['54', 'Caxias do Sul', 'RS'],
  ['55', 'Santa Maria', 'RS'],
  ['61', 'Brasília', 'DF'],
  ['62', 'Goiânia', 'GO'],
  ['63', 'Palmas', 'TO'],
  ['64', 'Rio Verde', 'GO'],
  ['65', 'Cuiabá', 'MT'],
  ['66', 'Rondonópolis', 'MT'],
  ['67', 'Campo Grande', 'MS'],
  ['68', 'Rio Branco', 'AC'],
  ['69', 'Porto Velho', 'RO'],
  ['71', 'Salvador', 'BA'],
  ['73', 'Ilhéus', 'BA'],
  ['74', 'Juazeiro', 'BA'],
  ['75', 'Feira de Santana', 'BA'],
  ['77', 'Barreiras', 'BA'],
  ['79', 'Aracaju', 'SE'],
  ['81', 'Recife', 'PE'],
  ['82', 'Maceió', 'AL'],
  ['83', 'João Pessoa', 'PB'],
  ['84', 'Natal', 'RN'],
  ['85', 'Fortaleza', 'CE'],
  ['86', 'Teresina', 'PI'],
  ['87', 'Petrolina', 'PE'],
  ['88', 'Juazeiro do Norte', 'CE'],
  ['89', 'Picos', 'PI'],
  ['91', 'Belém', 'PA'],
  ['92', 'Manaus', 'AM'],
  ['93', 'Santarém', 'PA'],
  ['94', 'Marabá', 'PA'],
  ['95', 'Boa Vista', 'RR'],
  ['96', 'Macapá', 'AP'],
  ['97', 'Coari', 'AM'],
  ['98', 'São Luís', 'MA'],
  ['99', 'Imperatriz', 'MA'],
];

/** Texto do DDD como aparece no formulário de captura ("41 - Curitiba/PR"). */
export function dddTitle(ddd: string): string {
  const row = DDD_TABLE.find(([code]) => code === ddd);
  return row ? `${row[0]} - ${row[1]}/${row[2]}` : ddd;
}

const STATE_ALIASES: Record<string, string> = { 'federal district': 'DF' };

export function stateFromRegion(region: string | undefined): BrazilState | undefined {
  const key = norm(region);
  if (!key) return undefined;
  const states = Object.values(BRAZIL_STATES);
  const alias = STATE_ALIASES[key];
  return states.find((s) => s.uf === alias || norm(s.name) === key || norm(s.uf) === key);
}

export function placeFrom(data: {
  city?: string;
  region?: string;
  country?: string;
  geo?: string;
}): UserPlace {
  const country = data.country ? data.country.toUpperCase() : undefined;
  const brazil = country === 'BR';
  const state = brazil ? stateFromRegion(data.region) : undefined;
  const ddds = state ? DDD_TABLE.filter(([, , uf]) => uf === state.uf).map(([code]) => code) : [];
  let ddd: string | undefined;
  if (state && data.city) {
    const city = norm(data.city);
    ddd = DDD_TABLE.find(
      ([, names, uf]) => uf === state.uf && names.split('|').some((n) => norm(n) === city),
    )?.[0];
  }
  if (!ddd && ddds.length === 1) ddd = ddds[0];
  return {
    city: data.city?.trim() || undefined,
    region: data.region?.trim() || undefined,
    country,
    geo: data.geo,
    state,
    ddd,
    ddds,
  };
}

/** Lugar da pessoa pelo IP (já guardado no primeiro carregamento; não faz nova consulta). */
export function userPlace(): UserPlace {
  const ctx = getRequestContext();
  return placeFrom({ city: ctx.city, region: ctx.region, country: ctx.country, geo: ctx.geo });
}

/** "Curitiba/PR" no Brasil; "Lisboa, Lisboa" fora; só a cidade quando falta o resto. */
export function cityLabel(place: UserPlace): string {
  if (!place.city) return '';
  if (place.state) return `${place.city}/${place.state.uf}`;
  if (place.region && norm(place.region) !== norm(place.city))
    return `${place.city}, ${place.region}`;
  return place.city;
}
